import { Fragment, useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  X,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { StageBadge } from "./StageBadge";
import { Button } from "@/components/ui/base/button";
import {
  parseHistory,
  formatIso,
  type MerchantLifecycle,
  type TrackState,
  type HistoryEntry,
} from "../types";

const client = generateClient<Schema>();

// Mirrors the `payout` metadata shape used by both referral and
// activation_bonus tracks. Both write `metadata.payout` with the same
// structure to MerchantLifecycle.tracks.<name>.metadata.
interface ReferralPayoutAttempt {
  attemptedAt: string;
  outcome: "succeeded" | "failed";
  amount: number;
  currency: string;
  stripeTransferId?: string;
  reason?: string;
  errorCode?: string;
  errorMessage?: string;
  durationMs: number;
  triggeredBy: string;
}

interface ReferralPayoutMetadata {
  status: "pending" | "succeeded" | "failed";
  stripeTransferId?: string;
  succeededAt?: string;
  lastError?: string;
  attempts: ReferralPayoutAttempt[];
}

interface ReferralIneligibilityMetadata {
  reason: string;
  detectedAt: string;
  conflictingBusinessIds: string[];
  triggeredBy: string;
}

const REFERRAL_TRACK_NAME = "referral";
const ACTIVATION_BONUS_TRACK_NAME = "activation_bonus";

const PAYOUT_TRACKS = new Set([
  REFERRAL_TRACK_NAME,
  ACTIVATION_BONUS_TRACK_NAME,
]);

const INELIGIBILITY_LABELS: Record<string, string> = {
  duplicate_identity: "Duplicate identity",
};

function parseIneligibilityMetadata(
  metadata: Record<string, unknown> | undefined,
): ReferralIneligibilityMetadata | null {
  const raw = metadata?.ineligibility;
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Partial<ReferralIneligibilityMetadata>;
  if (typeof candidate.reason !== "string") return null;
  return {
    reason: candidate.reason,
    detectedAt:
      typeof candidate.detectedAt === "string" ? candidate.detectedAt : "",
    conflictingBusinessIds: Array.isArray(candidate.conflictingBusinessIds)
      ? candidate.conflictingBusinessIds.filter(
          (s): s is string => typeof s === "string",
        )
      : [],
    triggeredBy:
      typeof candidate.triggeredBy === "string" ? candidate.triggeredBy : "",
  };
}

function parsePayoutMetadata(
  metadata: Record<string, unknown> | undefined,
): ReferralPayoutMetadata | null {
  const payout = metadata?.payout;
  if (!payout || typeof payout !== "object") return null;
  const candidate = payout as Partial<ReferralPayoutMetadata>;
  if (
    candidate.status !== "pending" &&
    candidate.status !== "succeeded" &&
    candidate.status !== "failed"
  ) {
    return null;
  }
  return {
    status: candidate.status,
    stripeTransferId:
      typeof candidate.stripeTransferId === "string"
        ? candidate.stripeTransferId
        : undefined,
    succeededAt:
      typeof candidate.succeededAt === "string"
        ? candidate.succeededAt
        : undefined,
    lastError:
      typeof candidate.lastError === "string" ? candidate.lastError : undefined,
    attempts: Array.isArray(candidate.attempts)
      ? (candidate.attempts as ReferralPayoutAttempt[])
      : [],
  };
}

function truncateMiddle(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

function formatAmount(cents: number, currency: string): string {
  const symbol = currency.toLowerCase() === "usd" ? "$" : "";
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

const SEVERITY_MAP: Record<string, { label: string; className: string }> = {
  verified: { label: "OK — no issues", className: "text-green-700" },
  pending: {
    label: "Incomplete — verification in progress",
    className: "text-muted-foreground",
  },
  not_started: {
    label: "Incomplete — not started",
    className: "text-muted-foreground",
  },
  issues: { label: "Warning — causes suspended", className: "text-amber-700" },
  ok: { label: "OK — no issues", className: "text-green-700" },
  input_required: {
    label: "Warning — Stripe needs more information",
    className: "text-amber-700",
  },
  payouts_paused: {
    label: "Blocked — payouts paused",
    className: "text-red-700",
  },
  charges_blocked: {
    label: "Blocked — charges disabled",
    className: "text-red-700",
  },
};

const DOT_COLORS: Record<string, string> = {
  verified: "bg-green-500",
  pending: "bg-blue-400",
  not_started: "bg-muted-foreground/50",
  issues: "bg-yellow-500",
  restricted: "bg-red-500",

  // Referral stages — keep in sync with StageBadge.tsx
  qualifying: "bg-blue-500",
  eligible: "bg-yellow-500",
  rewarded: "bg-green-500",

  // Activation bonus stages
  paid: "bg-green-500",
  expired: "bg-muted-foreground",
  failed: "bg-red-500",

  // Account health stages — keep in sync with StageBadge.tsx
  ok: "bg-green-500",
  input_required: "bg-yellow-500",
  payouts_paused: "bg-red-500",
  charges_blocked: "bg-red-500",
};

interface TrackSheetProps {
  trackName: string;
  trackState: TrackState;
  lifecycle: MerchantLifecycle;
  onClose: () => void;
  onLifecycleRefresh: () => void;
}

export function TrackSheet({
  trackName,
  trackState,
  lifecycle,
  onClose,
  onLifecycleRefresh,
}: TrackSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Filter history for this track. When multiple entries share the same
  // `triggeredBy` (e.g. a transition followed by an inline same-stage
  // metadata update written in the same handler invocation), prefer the
  // entry that represents a real stage change (`from !== to`) so the
  // timeline doesn't show a duplicate "eligible → eligible" next to the
  // "qualifying → eligible" transition it caused.
  const allHistory = parseHistory(lifecycle.history);
  const dedupedByTrigger = new Map<string, HistoryEntry>();
  for (const entry of allHistory) {
    if (entry.track !== trackName) continue;
    const existing = dedupedByTrigger.get(entry.triggeredBy);
    if (!existing) {
      dedupedByTrigger.set(entry.triggeredBy, entry);
      continue;
    }
    // Prefer the entry with a real stage change.
    const existingIsNoOp = existing.from === existing.to;
    const entryIsNoOp = entry.from === entry.to;
    if (existingIsNoOp && !entryIsNoOp) {
      dedupedByTrigger.set(entry.triggeredBy, entry);
    }
  }
  const trackHistory = [...dedupedByTrigger.values()].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  const metadata = trackState.metadata as Record<string, unknown> | undefined;
  const currentlyDue = metadata?.currentlyDue as string[] | undefined;

  const payout = PAYOUT_TRACKS.has(trackName)
    ? parsePayoutMetadata(metadata)
    : null;
  const ineligibility =
    trackName === REFERRAL_TRACK_NAME && trackState.stage === "ineligible"
      ? parseIneligibilityMetadata(metadata)
      : null;

  const severity = SEVERITY_MAP[trackState.stage];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/10" onClick={onClose} />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="border-border bg-card fixed top-0 right-0 bottom-0 z-50 flex w-[640px] max-w-[95vw] flex-col border-l shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2.5">
            <h2 className="text-foreground text-sm font-bold uppercase">
              {trackName} Track
            </h2>
            <StageBadge stage={trackState.stage} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-subtle-foreground hover:bg-muted hover:text-muted-foreground rounded p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Current state */}
          <div className="mb-5">
            <h3 className="text-muted-foreground mb-2.5 text-xs font-semibold tracking-wide uppercase">
              Current State
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Stage</span>
                <StageBadge stage={trackState.stage} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Since</span>
                <span className="text-foreground">
                  {formatIso(trackState.enteredAt)}
                </span>
              </div>
              {severity && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Overall Impact</span>
                  <span className={severity.className}>{severity.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Requirements (when present) */}
          {currentlyDue && currentlyDue.length > 0 && (
            <div className="mb-5 rounded-md border border-yellow-200 bg-amber-50 px-3.5 py-3">
              <div className="mb-2 text-xs font-semibold text-amber-800">
                Outstanding Requirements ({currentlyDue.length})
              </div>
              <div className="flex flex-col gap-1.5">
                {currentlyDue.map((req) => (
                  <span
                    key={req}
                    className="rounded bg-amber-100 px-2 py-1 font-mono text-xs text-amber-900"
                  >
                    {req}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Ineligibility banner (referral track only) */}
          {ineligibility ? (
            <IneligibilityBanner
              ineligibility={ineligibility}
              businessId={lifecycle.businessId}
              onOverrideComplete={onLifecycleRefresh}
            />
          ) : null}

          {/* Payout status (referral + activation_bonus) */}
          {payout && (
            <PayoutBanner
              payout={payout}
              trackName={trackName}
              businessId={lifecycle.businessId}
              onRetryComplete={onLifecycleRefresh}
            />
          )}
          {payout && payout.attempts.length >= 1 && (
            <PayoutAttemptsTable attempts={payout.attempts} />
          )}

          {/* Timeline */}
          <div>
            <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
              History
            </h3>
            {trackHistory.length === 0 ? (
              <p className="text-subtle-foreground text-sm">
                No transitions recorded.
              </p>
            ) : (
              <div className="relative pl-5">
                <div className="bg-muted absolute top-1.5 bottom-1.5 left-[5px] w-px" />
                {trackHistory.map((entry, idx) => (
                  <TimelineEntry
                    key={idx}
                    entry={entry}
                    isFirst={idx === 0}
                    payout={payout}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function IneligibilityBanner({
  ineligibility,
  businessId,
  onOverrideComplete,
}: {
  ineligibility: ReferralIneligibilityMetadata;
  businessId: string;
  onOverrideComplete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);

  async function handleConfirm() {
    setPending(true);
    try {
      const { data, errors } =
        await client.mutations.overrideReferralIneligibility({
          input: { businessId, note: note.trim() || undefined },
        });
      if (errors?.length) throw new Error(errors[0].message);
      const newStage = data?.newStage ?? null;
      const payoutStatus = data?.payoutStatus ?? null;

      if (newStage === "rewarded" && payoutStatus === "succeeded") {
        toast.success("Override succeeded — referee paid out");
      } else if (newStage === "eligible" && payoutStatus === "failed") {
        toast("Override succeeded; payout failed — use Retry", {
          icon: "⚠️",
          style: {
            background: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fde68a",
          },
        });
      } else {
        toast("Override triggered — check payout status", {
          icon: "⏳",
          style: {
            background: "#dbeafe",
            color: "#1e40af",
            border: "1px solid #bfdbfe",
          },
        });
      }
      setOpen(false);
      onOverrideComplete();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Override failed");
      // Modal stays open for retry
    } finally {
      setPending(false);
    }
  }

  const label =
    INELIGIBILITY_LABELS[ineligibility.reason] ?? ineligibility.reason;
  const detectedLabel = ineligibility.detectedAt
    ? formatIso(ineligibility.detectedAt)
    : "";

  return (
    <>
      <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-3.5 py-3">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-red-800 uppercase">
          <XCircle className="h-3.5 w-3.5" />
          Ineligible · {label}
          {detectedLabel ? (
            <span className="ml-1 text-red-700">
              · detected {detectedLabel}
            </span>
          ) : null}
        </div>
        {ineligibility.conflictingBusinessIds.length > 0 ? (
          <div className="mt-1 text-sm text-red-900">
            Conflicts with:{" "}
            {ineligibility.conflictingBusinessIds.map((id, idx) => (
              <Fragment key={id}>
                <a
                  href={`/merchants/${id}`}
                  className="underline hover:text-red-700"
                >
                  {id}
                </a>
                {idx < ineligibility.conflictingBusinessIds.length - 1
                  ? " · "
                  : null}
              </Fragment>
            ))}
          </div>
        ) : null}
        <div className="mt-2">
          <Button size="sm" variant="destructive" onClick={() => setOpen(true)}>
            Override &amp; Pay Out
          </Button>
        </div>
      </div>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-card w-full max-w-md rounded-md p-5 shadow-lg">
            <h3 className="text-foreground text-base font-semibold">
              Override Ineligibility
            </h3>
            <p className="text-foreground mt-2 text-sm">This will:</p>
            <ul className="text-foreground mt-1 list-disc pl-5 text-sm">
              <li>Transition the referee from ineligible → eligible</li>
              <li>Fire the referrer&apos;s Cello payout</li>
              <li>Attempt a $25 Stripe Transfer to the referee</li>
            </ul>
            <label className="text-foreground mt-4 block text-sm">
              Reason (optional):
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="border-border mt-1 w-full rounded border px-2 py-1 text-sm"
                disabled={pending}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleConfirm}
                disabled={pending}
              >
                {pending ? "Overriding…" : "Override & Pay Out"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PayoutBanner({
  payout,
  trackName,
  businessId,
  onRetryComplete,
}: {
  payout: ReferralPayoutMetadata;
  trackName: string;
  businessId: string;
  onRetryComplete: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const [optimisticPending, setOptimisticPending] = useState(false);

  async function handleRetry() {
    setRetrying(true);
    setOptimisticPending(true);
    try {
      const { data, errors } =
        trackName === ACTIVATION_BONUS_TRACK_NAME
          ? await client.mutations.RetryActivationBonusPayout({
              input: { businessId },
            })
          : await client.mutations.retryReferralRewardPayout({
              input: { businessId },
            });
      if (errors?.length) throw new Error(errors[0].message);
      const rawStatus = data?.status ?? "pending";
      // Normalize activation_bonus statuses to the referral vocabulary used
      // by the toast switch below.
      const status =
        rawStatus === "still_failed"
          ? "failed"
          : rawStatus === "cap_reached" || rawStatus === "ineligible"
            ? "failed"
            : rawStatus;
      const message = `Retry triggered — ${status}`;
      if (status === "succeeded") {
        toast.success(message);
      } else if (status === "failed") {
        toast(message, {
          icon: "⚠️",
          style: {
            background: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fde68a",
          },
        });
      } else {
        toast(message, {
          icon: "⏳",
          style: {
            background: "#dbeafe",
            color: "#1e40af",
            border: "1px solid #bfdbfe",
          },
        });
      }
      onRetryComplete();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    } finally {
      setRetrying(false);
      setOptimisticPending(false);
    }
  }

  // Render pending banner while the retry mutation is in-flight. Reset in
  // `finally` so the real post-retry status (succeeded / failed / still
  // pending) lands as soon as the parent refetch provides fresh metadata.
  if (optimisticPending) {
    return (
      <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 px-3.5 py-3">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-blue-800 uppercase">
          <Clock className="h-3.5 w-3.5" />
          Payout · Pending
        </div>
        <div className="mt-1 text-sm text-blue-900">Retry in progress…</div>
      </div>
    );
  }

  const latestSuccessful = [...payout.attempts]
    .reverse()
    .find((attempt) => attempt.outcome === "succeeded");

  if (payout.status === "succeeded") {
    const amount = latestSuccessful
      ? formatAmount(latestSuccessful.amount, latestSuccessful.currency)
      : null;
    const transferId =
      payout.stripeTransferId ?? latestSuccessful?.stripeTransferId;
    return (
      <div className="mb-5 rounded-md border border-green-200 bg-green-50 px-3.5 py-3">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-green-800 uppercase">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Payout · Succeeded
        </div>
        <div className="mt-1.5 space-y-1 text-sm text-green-900">
          {amount && (
            <div className="flex justify-between">
              <span className="text-green-700">Amount</span>
              <span className="font-semibold">{amount}</span>
            </div>
          )}
          {transferId && (
            <div className="flex justify-between">
              <span className="text-green-700">Transfer</span>
              <span className="font-mono text-xs">
                {truncateMiddle(transferId, 12)}
              </span>
            </div>
          )}
          {payout.succeededAt && (
            <div className="flex justify-between">
              <span className="text-green-700">At</span>
              <span>{formatIso(payout.succeededAt)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (payout.status === "pending") {
    return (
      <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 px-3.5 py-3">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-blue-800 uppercase">
          <Clock className="h-3.5 w-3.5" />
          Payout · Pending
        </div>
        <div className="mt-1 text-sm text-blue-900">Attempt in progress.</div>
      </div>
    );
  }

  // failed
  const lastAttempt = payout.attempts[payout.attempts.length - 1];
  const reason = lastAttempt?.reason;
  return (
    <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-3.5 py-3">
      <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-red-800 uppercase">
        <XCircle className="h-3.5 w-3.5" />
        Payout · Failed
      </div>
      <div className="mt-1.5 space-y-1 text-sm text-red-900">
        {reason && (
          <div className="flex justify-between gap-3">
            <span className="text-red-700">Reason</span>
            <span className="font-mono text-xs break-all">{reason}</span>
          </div>
        )}
        {payout.lastError && (
          <div className="text-xs break-words text-red-800">
            Last error: &ldquo;{payout.lastError}&rdquo;
          </div>
        )}
      </div>
      <div className="mt-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={retrying}
          onClick={() => {
            void handleRetry();
          }}
        >
          {retrying ? "Retrying…" : "Retry"}
        </Button>
      </div>
    </div>
  );
}

function PayoutAttemptsTable({
  attempts,
}: {
  attempts: ReferralPayoutAttempt[];
}) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  function toggleRow(index: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  return (
    <div className="mb-5">
      <h3 className="text-muted-foreground mb-2.5 text-xs font-semibold tracking-wide uppercase">
        Payout Attempts ({attempts.length})
      </h3>
      <div className="border-border overflow-x-auto rounded-md border">
        <table className="min-w-full text-xs">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th
                scope="col"
                aria-label="Expand row"
                className="w-6 px-1 py-1.5"
              />
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                #
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Attempted At
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Outcome
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Duration
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Reason
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Error Code
              </th>
              <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                Triggered By
              </th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((attempt, idx) => {
              const isExpanded = expanded.has(idx);
              const isSucceeded = attempt.outcome === "succeeded";
              return (
                <Fragment key={idx}>
                  <tr
                    onClick={() => toggleRow(idx)}
                    className="border-border hover:bg-muted cursor-pointer border-t align-top"
                  >
                    <td className="text-subtle-foreground w-6 px-1 py-1.5">
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </td>
                    <td className="text-muted-foreground px-2 py-1.5 whitespace-nowrap">
                      {idx + 1}
                    </td>
                    <td className="text-foreground px-2 py-1.5 whitespace-nowrap">
                      {new Date(attempt.attemptedAt).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span
                        className={
                          isSucceeded
                            ? "inline-block rounded bg-green-100 px-1.5 py-0.5 font-semibold text-green-800"
                            : "inline-block rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-800"
                        }
                      >
                        {attempt.outcome}
                      </span>
                    </td>
                    <td className="text-foreground px-2 py-1.5 whitespace-nowrap">
                      {`${(attempt.durationMs / 1000).toFixed(1)}s`}
                    </td>
                    <td className="text-foreground px-2 py-1.5 font-mono whitespace-nowrap">
                      {isSucceeded ? "—" : (attempt.reason ?? "—")}
                    </td>
                    <td className="text-foreground px-2 py-1.5 font-mono whitespace-nowrap">
                      {isSucceeded ? "—" : (attempt.errorCode ?? "—")}
                    </td>
                    <td className="text-foreground px-2 py-1.5 font-mono whitespace-nowrap">
                      {attempt.triggeredBy}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-border bg-muted border-t">
                      <td colSpan={8} className="px-2 py-2">
                        <pre className="overflow-x-auto rounded bg-slate-900 p-2 text-[11px] text-slate-100">
                          {JSON.stringify(attempt, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TimelineEntry({
  entry,
  isFirst,
  payout,
}: {
  entry: HistoryEntry;
  isFirst: boolean;
  payout: ReferralPayoutMetadata | null;
}) {
  const dotColor = DOT_COLORS[entry.to] ?? "bg-muted-foreground";

  // When this timeline entry was produced by a payout attempt, surface the
  // matching attempt's details (attempt number, total, outcome, exact time).
  // The dedupe logic upstream collapses repeated triggeredBy values to a
  // single timeline entry, but the AttemptRecord array keeps each retry
  // separately — so grab the LATEST matching attempt for this trigger.
  const matchingAttempts =
    payout?.attempts.filter((a) => a.triggeredBy === entry.triggeredBy) ?? [];
  const latestAttempt =
    matchingAttempts.length > 0
      ? matchingAttempts[matchingAttempts.length - 1]
      : null;
  const attemptIndex =
    latestAttempt && payout ? payout.attempts.indexOf(latestAttempt) : -1;
  const attemptNumber = attemptIndex >= 0 ? attemptIndex + 1 : null;
  const totalAttempts = payout?.attempts.length ?? 0;

  const displayTimestamp = latestAttempt?.attemptedAt ?? entry.timestamp;

  return (
    <div className={`relative pb-4 ${isFirst ? "" : ""}`}>
      <div
        className={`ring-card absolute top-1.5 -left-[13px] h-2.5 w-2.5 rounded-full ring-2 ${dotColor}`}
      />
      <div className="flex items-center gap-2">
        <div className="text-foreground text-sm font-medium">
          {entry.to.replace(/_/g, " ")}
        </div>
        {latestAttempt && (
          <span
            className={
              latestAttempt.outcome === "succeeded"
                ? "inline-block rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-800"
                : "inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800"
            }
          >
            {latestAttempt.outcome}
          </span>
        )}
      </div>
      <div className="text-subtle-foreground text-xs">
        from {entry.from.replace(/_/g, " ")}
      </div>
      {latestAttempt && attemptNumber && (
        <div className="text-muted-foreground text-xs">
          Attempt {attemptNumber} of {totalAttempts}
          {latestAttempt.reason ? ` · ${latestAttempt.reason}` : ""}
        </div>
      )}
      <div className="text-muted-foreground mt-0.5 font-mono text-[11px] break-all">
        {entry.triggeredBy}
      </div>
      <div className="text-subtle-foreground text-xs">
        {formatIso(displayTimestamp)}
      </div>
    </div>
  );
}
